import type { StoryModel } from './story-model';
import { activePeriods } from './story-model';
import type { PeriodTrackLayout } from './story-period-tracks';
import { buildTimeWheelTicks, chooseTickGranularity } from './time-wheel-ticks';
import { periodAccentRgb, STORY_PIXELS_PER_UNIT } from './story-dom';

interface DrawTimeWheelParams {
  readonly model: StoryModel;
  readonly position: number;
  readonly width: number;
  readonly height: number;
  readonly dpr: number;
  readonly periodTracks: PeriodTrackLayout;
  readonly reducedMotion: boolean;
}

const BASE_TEXT_COLOR = '237, 237, 240';
export function drawTimeWheel(
  ctx: CanvasRenderingContext2D,
  { model, position, width, height, dpr, periodTracks, reducedMotion }: DrawTimeWheelParams,
): void {
  if (width <= 0 || height <= 0 || dpr <= 0) return;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);
  const currentPeriods = activePeriods(model, position);
  const periodColors = currentPeriods.map((entry) => entry.period.color);
  const [accentR, accentG, accentB] = periodAccentRgb(periodColors);
  const accentColor = `rgb(${accentR}, ${accentG}, ${accentB})`;
  const lineX = width - 26;
  drawBackdrop(ctx, width, height);
  drawPeriodBands(ctx, model, periodTracks, position, width, height, reducedMotion);
  drawFocusGuide(ctx, height, reducedMotion, lineX, accentColor);
  drawTicks(ctx, model, position, width, height, reducedMotion, lineX);
  ctx.restore();
}

function drawBackdrop(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const gradient = ctx.createLinearGradient(0, 0, width, 0);
  gradient.addColorStop(0, 'rgba(17, 18, 24, 0.8)');
  gradient.addColorStop(1, 'rgba(26, 27, 35, 0.95)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

function drawPeriodBands(
  ctx: CanvasRenderingContext2D,
  model: StoryModel,
  periodTracks: PeriodTrackLayout,
  position: number,
  width: number,
  height: number,
  reducedMotion: boolean,
): void {
  if (model.periods.length === 0 || periodTracks.trackCount === 0) return;
  const centerY = height * 0.5;
  const bandWidth = 7;
  const trackGap = 4;
  const startX = width - 20;
  const alpha = reducedMotion ? 0.18 : 0.28;

  for (const item of periodTracks.tracks) {
    const fromY = centerY + (item.period.fromPosition - position) * STORY_PIXELS_PER_UNIT;
    const toY = centerY + (item.period.toPosition - position) * STORY_PIXELS_PER_UNIT;
    const top = clamp(Math.min(fromY, toY), -32, height + 32);
    const bottom = clamp(Math.max(fromY, toY), -32, height + 32);
    if (bottom < 0 || top > height) continue;
    const bandHeight = Math.max(6, bottom - top);
    const x = startX - item.track * (bandWidth + trackGap);
    ctx.fillStyle = withAlpha(item.period.period.color, alpha);
    roundRect(ctx, x, top, bandWidth, bandHeight, bandWidth * 0.5);
    ctx.fill();
  }
}

function drawFocusGuide(
  ctx: CanvasRenderingContext2D,
  height: number,
  reducedMotion: boolean,
  lineX: number,
  accentColor: string,
): void {
  const focusY = height * 0.5;
  const glow = reducedMotion ? 8 : 14;
  ctx.strokeStyle = withOpacity(accentColor, 0.85);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(lineX - 22, focusY);
  ctx.lineTo(lineX + 10, focusY);
  ctx.stroke();

  const grad = ctx.createRadialGradient(lineX - 4, focusY, 0, lineX - 4, focusY, glow);
  grad.addColorStop(0, withOpacity(accentColor, 0.95));
  grad.addColorStop(1, withOpacity(accentColor, 0));
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(lineX - 4, focusY, glow, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = accentColor;
  ctx.beginPath();
  ctx.arc(lineX - 4, focusY, 3.4, 0, Math.PI * 2);
  ctx.fill();
}

function drawTicks(
  ctx: CanvasRenderingContext2D,
  model: StoryModel,
  position: number,
  width: number,
  height: number,
  reducedMotion: boolean,
  lineX: number,
): void {
  const visibleUnits = Math.max(0.2, (height * 0.5) / STORY_PIXELS_PER_UNIT);
  const startPosition = Math.max(0, position - visibleUnits);
  const endPosition = Math.min(model.length, position + visibleUnits);
  const startMs = model.scale.toTime(startPosition);
  const endMs = model.scale.toTime(endPosition);
  const granularity = chooseTickGranularity(startMs, endMs);
  const ticks = buildTimeWheelTicks(startMs, endMs, granularity);
  const focusY = height * 0.5;
  const falloff = height * (reducedMotion ? 0.38 : 0.52);
  const arcDepth = reducedMotion ? 20 : 36;
  const occupiedLabelYs: number[] = [];

  for (const tick of ticks) {
    const tickPosition = model.scale.toPosition(tick.epochMs);
    const y = focusY + (tickPosition - position) * STORY_PIXELS_PER_UNIT;
    if (y < -30 || y > height + 30) continue;
    const distance = Math.abs(y - focusY);
    const normalized = clamp(distance / Math.max(1, falloff), 0, 1);
    const proximity = Math.cos(normalized * (Math.PI / 2));
    const hierarchyBoost = tick.hierarchy === 'major' ? 1 : 0.68;
    const alpha = clamp((0.15 + proximity * 0.8) * hierarchyBoost, 0.1, 1);
    const fontSize =
      (tick.hierarchy === 'major' ? 14 : 11) + proximity * (tick.hierarchy === 'major' ? 7 : 4);
    const x = lineX - 42 - proximity * arcDepth;
    const tickLength = 7 + proximity * 9;
    const minLabelGap =
      tick.hierarchy === 'major' ? Math.max(20, fontSize * 1.05) : Math.max(13, fontSize * 0.9);
    if (isLabelCrowded(occupiedLabelYs, y, minLabelGap)) continue;

    ctx.strokeStyle = `rgba(${BASE_TEXT_COLOR}, ${clamp(alpha * 0.8, 0.1, 1).toFixed(3)})`;
    ctx.lineWidth = tick.hierarchy === 'major' ? 1.5 : 1;
    ctx.beginPath();
    ctx.moveTo(x + 8, y);
    ctx.lineTo(x + 8 + tickLength, y);
    ctx.stroke();

    ctx.fillStyle = `rgba(${BASE_TEXT_COLOR}, ${alpha.toFixed(3)})`;
    ctx.font = `${fontSize.toFixed(1)}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(tick.label, x, y);
    occupiedLabelYs.push(y);
  }
}

function isLabelCrowded(occupiedLabelYs: readonly number[], y: number, minGap: number): boolean {
  for (const otherY of occupiedLabelYs) {
    if (Math.abs(otherY - y) < minGap) {
      return true;
    }
  }
  return false;
}

function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

function withOpacity(rgbColor: string, alpha: number): string {
  const parts = rgbColor.replace('rgb(', '').replace(')', '').split(',');
  if (parts.length !== 3) return `rgba(99, 102, 241, ${alpha.toFixed(3)})`;
  const r = Number.parseInt(parts[0]?.trim() ?? '', 10);
  const g = Number.parseInt(parts[1]?.trim() ?? '', 10);
  const b = Number.parseInt(parts[2]?.trim() ?? '', 10);
  if (![r, g, b].every(Number.isFinite)) return `rgba(99, 102, 241, ${alpha.toFixed(3)})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

function parseHex(hex: string): [number, number, number] {
  const value = hex.startsWith('#') ? hex.slice(1) : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return [99, 102, 241];
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
